import { useState } from "react";

import { DOCUMENT_NAME_VALUES } from "@core/core.constants";
import type { DocumentInformation, DocumentNameValue } from "@core/core.types";
import type { DocumentDownloadInfo } from "@services/apiEcms/apiEcms.types";
import { useGetDocumentsGroupSearch } from "@services/apiEcms/useGetDocumentsGroupSearch";
import { usePostDocumentsGroupDownload } from "@services/apiEcms/usePostDocumentsGroupDownload";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import type { DocumentRow } from "./DocumentsTable.types";
import { formatDocumentsByCategory } from "./DocumentsTable.utils";

/**
 * Will generate all the table rows from a list of document
 * those rows will be formatted to fit TableDocuments component expectanties
 * @param documents all the documents received from fetch
 * @returns an array fitting for Table component, with data related to documents
 */
const useFormatDocuments = (
  documents: DocumentInformation[] | undefined,
): DocumentRow[] => {
  const { t } = useCustomTranslation("main");

  if (!documents) return [];

  const agregatedDocuments = formatDocumentsByCategory(documents);

  return Object.entries(agregatedDocuments).map(
    ([name, documentsByFileExtension]) => {
      const categoryName =
        (name as DocumentNameValue) || DOCUMENT_NAME_VALUES.OTHER;
      const latestPdfDocument = documentsByFileExtension.documentsPdf.sort(
        (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime(),
      )[0];
      const latestXlsDocument = documentsByFileExtension.documentsXls.sort(
        (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime(),
      )[0];

      return {
        name: t(`documents.table.documentType.${categoryName}`),
        pdf: latestPdfDocument,
        xls: latestXlsDocument,
      };
    },
  );
};

export const useDocumentDownloadAction = (supportEmail: string) => {
  const {
    data: documentsData,
    isError: isGetDocumentsError,
    refetch: documentRefetch,
  } = useGetDocumentsGroupSearch();

  const [processingId, setProcessingId] = useState("");
  const mutation = usePostDocumentsGroupDownload({ supportEmail });

  const mutateFunction = async ({
    fileName,
    s3VersionID,
    fileExtension,
  }: DocumentDownloadInfo) => {
    setProcessingId(s3VersionID);
    try {
      const data = await mutation.mutateAsync({
        fileName,
        s3VersionID,
        fileExtension,
      });
      return data;
    } catch (error) {
      return error;
    }
  };

  const formattedDocuments = useFormatDocuments(
    documentsData?.data?.groupDocuments,
  );

  return {
    mutationStatus: documentsData?.status ?? mutation.error?.response?.status,
    mutateFunction,
    processingId,
    formattedDocuments,
    isGetDocumentsError,
    documentRefetch,
  };
};
